<template>
  <v-form ref="form" v-model="formValid" class="pb-3">
    <div v-for="panel in panelsToShow" :key="panel.code">
      <v-row v-if="panel.label && showPanelLabels" class="mx-0 mb-5 grey darken-3">
        <v-card-title>
          <h3 class="white--text" v-html="panel.label"></h3>
        </v-card-title>
      </v-row>
      <v-row v-for="field in panel.fields" v-show="show(field.code)" v-if="!forceRenderer && !hiddenFields.includes(field.code)" :key="field.code" class="mx-2">
        <v-col cols="12 alert-border" :class="[{'warning-border': field.modifiedWarning}, {'encrypted-border': field.encrypted}]" class="py-1">
          <div v-if="field.modifiedWarning" class="white--text font-weight-bold span-position warning-bg">Ne pas modifier!</div>
          <div v-if="field.encrypted" class="white--text font-weight-bold span-position encrypted-bg"> <v-icon>mdi-lock</v-icon> Champs chiffrés</div>

          <!--- Field code: {{ field.code }} <br>
          Content:  {{ newContent[field.code] }} -->
          <!-- Simple fields  -->
          <SimpleField
            v-if="isSimpleField(field)"
            v-model="newContent[field.code]"
            :field="field"
          />

          <RTE
            v-if="field.viewType === 'RTE'"
            v-model="newContent[field.code]"
            :field="field"
          />

          <DependentSelect
            v-if="field.viewType === 'DependentSelect'"
            v-model="newContent[field.code]"
            :field="field"
            :dependent-value="newContent[field.dependentOn]"
          />

          <EntitySelect
            v-if="field.viewType === 'EntitySelect'"
            v-model="newContent[field.code]"
            :field="field"
          />

          <!-- TextAreaHTML field -->
          <TextAreaHTMLField
            v-if="field.viewType === 'TextareaHTML'"
            v-model="newContent[field.code]"
            :field="field"
          />

          <!-- TextAreaHTML field -->
          <CustomTextField
            v-if="field.viewType === 'CustomTextField'"
            v-model="newContent[field.code]"
            :field="field"
          />

          <!-- Text JSON field-->
          <CodeEditorJSON
            v-if="field.viewType === 'CodeEditorJSON'"
            v-model="newContent[field.code]"
            :label="field.label"
          />

          <!-- Text JSON field-->
          <CodeEditorHTML
            v-if="field.viewType === 'CodeEditorHTML'"
            v-model="newContent[field.code]"
            :field="field"
          />

          <!-- CodeEditor field -->
          <CodeEditorField
            v-if="field.viewType === 'CodeEditorField'"
            v-model="newContent[field.code]"
            :field="field"
          />

          <!-- Date and time picker -->
          <DTPicker
            v-if="isDTPicker(field)"
            v-model="newContent[field.code]"
            :field="field"
          />

          <!-- ColorPicker -->
          <ColorPicker
            v-if="field.viewType === 'ColorPicker'"
            v-model="newContent[field.code]"
            :field="field"
          />

          <!-- Picker -->
          <Picker
            v-if="field.viewType === 'Picker'"
            v-model="newContent[field.code]"
            :field="field"
            :content="newContent"
            :raw-content-types="rawContentTypes"
            @updateNewContent="updateNewContent"
          />

          <!-- Picker External -->
          <PickerExternal
            v-if="field.viewType === 'PickerExternal'"
            v-model="newContent[field.code]"
            :field="field"
            :content="newContent"
            @updateNewContent="updateNewContent"
          />

          <!-- Cloudinary -->
          <Cloudinary
            v-if="field.viewType === 'Cloudinary'"
            v-model="newContent[field.code]"
            :field="field"
            :full-form="showPanelLabels"
          />
          <CloudinarySEO
            v-if="field.viewType === 'CloudinarySEO'"
            v-model="newContent[field.code]"
            :field="field"
            :full-form="showPanelLabels"
            :raw-content-types="rawContentTypes"
          />

          <!-- Entity  -->
          <Entity
            v-if="field.viewType === 'Entity'"
            v-model="newContent[field.code]"
            :field="field"
            :raw-content-types="rawContentTypes"
          />

          <CustomFields
            v-if="field.viewTypeCustom"
            v-model="newContent[field.code]"
            :field="field"
            :form-content="newContent"
            :raw-content-types="rawContentTypes"
          />
        </v-col>
      </v-row>
    </div>
  </v-form>
</template>
<script setup>

import gql from 'graphql-tag';
import Vue, { ref, computed, watch, onMounted, nextTick } from 'vue';

import { useStore } from 'vue2-helpers/vuex';
import { useRoute, useRouter } from 'vue2-helpers/vue-router';

import { useQuery } from '@vue/apollo-composable';
import SimpleField from './SimpleField.vue';
import RTE from './RTE.vue';

import TextAreaHTMLField from './TextAreaHTMLField.vue';
import CustomTextField from './CustomTextField.vue';
import CodeEditorField from './CodeEditorField.vue';
import DTPicker from './DTPicker.vue';
import Cloudinary from './Cloudinary/Cloudinary.vue';
import CloudinarySEO from './Cloudinary/CloudinarySEO.vue';
import Picker from './Picker.vue';
import PickerExternal from './PickerExternal.vue';
import Entity from './Entity.vue';
import EntitySelect from './EntitySelect.vue';
import ColorPicker from './ColorPicker.vue';
import CustomFields from './CustomFields.vue';
import CodeEditorJSON from './CodeEditorJSON';
import CodeEditorHTML from './CodeEditorHTML';
import DependentSelect from './DependentSelect';

import { difference } from '@/utils/content';

const { openSearchLiveContentFilters } = require('@/utils/merch');

const emit = defineEmits(['update:contentValid']);

// eslint-disable-next-line no-undef
const props = defineProps({
  contentType: Object,
  contentValid: Boolean,
  content: Object,
  fieldsToShow: Array,
  rawContentTypes: Object,
  showPanelLabels: {
    type: Boolean,
    default: false
  }
});

const store = useStore();
const route = useRoute();

const panels = ref([]);

const panelsToShow = computed(() => panels.value.filter(p => !p.fields.every(field => hiddenFields.value.includes(field.code))));

const newContent = ref(props.content);
const formValid = ref(props.contentValid);
const forceRenderer = ref(false);

const updateNewContent = newContentArg => {
  forceRenderer.value = true;
  Object.assign(newContent.value, newContentArg);
  nextTick(() => {
    forceRenderer.value = false;
  });
};

const show = code => !props.fieldsToShow || props.fieldsToShow.includes(code);
// Check if is a simple field
const isSimpleField = field => {
  const fields = ['Text', 'Number', 'Select', 'Slider', 'Textarea', 'Checkbox', 'Switch', 'Radio button'];
  return fields.includes(field.viewType);
};
// Check if is a simple field
const isTextAreaHTMLField = field => {
  const fields = ['TextareaHTML'];
  return fields.includes(field.viewType);
};
// Check if is a Date or Time Picker
const isDTPicker = field => {
  const fields = ['Date Picker', 'Time Picker'];
  return fields.includes(field.viewType);
};

// Gets panels information for the form
const getPanels = (contentType, rawContentTypes) => {
  // Fields from content type
  const { fields } = contentType;

  const formFields = [];
  // console.log('getPanels fields', fields);
  // Runs the list of fields
  for (const key of Object.keys(fields)) {
    // Gets field object
    const field = { ...fields[key] };

    // check if is a CustomField to handle it on Custom component
    if (field.viewTypeCustom) {
      field.code = key;
      formFields.push(field);
    }

    if (field.hasOwnProperty('defaultValue') && (!newContent.value.hasOwnProperty(key) || newContent.value[key] === null)) {
      // Use Vue.set to keep reactivity
      Vue.set(newContent.value, key, field.defaultValue);
    }

    // Check if is a field to be displayed on the the form
    if (field.viewType) {
      // Define default properties with default values
      field.code = key;
      field.rules = [];
      field.dialog = false;

      // if doesn't have permission to write
      if (!store.getters.permissionToWrite) {
        field.readonly = true;
      }

      if (field.maxCharacters) {
        field.rules.push(v => (!v || v.length <= field.maxCharacters) || `Maximum ${field.maxCharacters} caractères`);
      }

      // If field is required
      if (!field.overrideMandatoryWhenEmpty && field.mandatory) {
        field.label += ' *';

        if (field.multivalued) {
          field.rules.push(v => (v && v.length !== 0) || 'Ce champ est obligatoire');
        }

        if (field.viewType === 'Number') {
          // Must be a number ( when is 0 must fall in this condition )
          field.rules.push(v => (v !== null && !Number.isNaN(v)) || 'Ce champ est obligatoire');
        } else {
          field.rules.push(v => !!v || 'Ce champ est obligatoire');
        }
      }

      if (field.viewType === 'Number') {
        // If field not required but type Int
        if (field.type === 'Int') {
          field.rules.push(v => (!v || (Number.isInteger(v))) || 'Vous ne pouvez renseigner que des nombres entiers (pas de virgule)');
        }
        if ((field.minNumber || field.minNumber === 0) && (field.maxNumber || field.maxNumber === 0)) {
          field.rules.push(v => (!v || (v >= field.minNumber && v <= field.maxNumber)) || `Le nombre doit être compris entre ${field.minNumber} et ${field.maxNumber}.`);
        } else {
          if (field.minNumber || field.minNumber === 0) {
            field.rules.push(v => (!v || (v >= field.minNumber)) || `Le nombre doit être supérieur ou égal à ${field.minNumber}`);
          }
          if (field.maxNumber || field.maxNumber === 0) {
            field.rules.push(v => (!v || (v <= field.maxNumber)) || `Le nombre doit être inférieur ou égal à ${field.maxNumber}`);
          }
        }
      }

      // If has a regex
      if (field.regex) {
        field.rules.push(v => {
          // Avoid verify regex if field is empty
          // use "mandatory: true" on field config if necessary
          if (!v) {
            return true;
          }
          return new RegExp(field.regex).test(v) || field.regexMsgFailed;
        });
      }

      if (field.multivalued === true && field.maxCount) {
        field.rules.push(v => (!v || (v && (v.length <= field.maxCount))) || 'Trop de valeurs sélectionnées');
      }

      /* If viewType of the field is a "entity" */
      if (field.viewType === 'Entity') {
        // Gets the custom header of the content type
        // const fieldContentType = getContentTypeByConfigArg('type', field.type, rawContentTypes);
        // field.headers = getCustomHeaders(fieldContentType);

        // // If has data on variation and received a variation id on parameters
        // if (newContent.value && newContent.value[key] && route.query.variation) {
        //   for (const index of Object.keys(newContent.value[key])) {
        //     if (newContent.value[key][index].uuid === route.query.variation) {
        //       field.value = newContent.value[key][index];
        //       field.index = index;
        //       field.dialog = true;
        //     }
        //   }
        // }
      } else if (field.viewType === 'EntitySelect') { // If it is a "EntitySelect" get's data
        field.options = [];
        field.loading = true;

        const searchFilters = [
          ...[{
            terms: {
              workspace: [
                store.getters.workspace.code
              ]
            }
          },
          {
            terms: {
              wtype: [
                field.selectWtype
              ]
            }
          }],
          ...openSearchLiveContentFilters()
        ];

        const searchVariables = {
          input: {
            query: { bool: { filter: searchFilters } },
            from: 0,
            size: 1000,
            sort: [
              { 'wtitle.raw': { order: 'asc' } }
            ]
          }
        };

        const { onResult } = useQuery(
          gql`query search($input:  ${store.state.environment.PROJECT_TYPE}OpenSearchFilterInput!) { genericEntities: ${store.state.environment.PROJECT}SearchOpenSearch(input: $input) { count items } }`,
          searchVariables,
          { fetchPolicy: 'no-cache' }
        );

        onResult(result => {
          if (result.data.genericEntities) {
            for (const option of result.data.genericEntities.items) {
              field.options.push({
                value: option.uuid,
                text: option.wtitle
              });
            }

            field.loading = false;
          }
        });
      }

      formFields.push(field);
    }
  }

  // Defines all panel objects
  const panelsList = [];

  // If content type has a panel order
  if (contentType.panelOrder) {
    contentType.panelOrder.forEach(label => {
      // Gets fields from the panel
      const panelFields = formFields.filter(field => field.panel === label).sort((a, b) => a.orderInPanel - b.orderInPanel);
      panelsList.push({ label: label, fields: panelFields });
    });
  }

  // Gets fields without panel
  const panelFields = formFields.filter(field => !field.hasOwnProperty('panel')).sort((a, b) => a.orderInPanel - b.orderInPanel);
  panelsList.push({ label: null, fields: panelFields });

  return panelsList;
};

const clonedContent = computed(() => JSON.parse(JSON.stringify(newContent.value)));

const hiddenFields = computed(() => {
  // Fields from content type
  let fieldsToHide = [];

  // Runs the list of fields
  Object.keys(props.contentType.fields).map(key => {
    // Gets field object
    const fieldConfig = props.contentType.fields[key];
    if (fieldConfig && fieldConfig.hideField) {
      fieldsToHide = fieldsToHide.concat(key);
    }

    // feature to hide field when a certain condition happen. This configuration come from API
    if (fieldConfig && fieldConfig.hideFieldsWhen) {
      const hideRuleFields = Array.isArray(fieldConfig.hideFieldsWhen) ? fieldConfig.hideFieldsWhen : [fieldConfig.hideFieldsWhen];

      let evalString = '';

      hideRuleFields.forEach(hideRule => {
        // New syntax: "length": "<= 3" for array length conditions
        if (hideRule.length && Array.isArray(newContent.value[key])) {
          evalString = `newContent.value[key].length ${hideRule.condition} ${hideRule.length}`;
        } else if (Array.isArray(hideRule.value)) {
          // this generic method hide fields when the value is an array
          // example: ["card", "unit"].every(i => i !== "card")
          evalString = `['${hideRule.value.join("','")}'].${hideRule.method}(i => i ${hideRule.condition} "${newContent.value[key]}")`;
        } else if (Array.isArray(newContent.value[key])) {
          // generic method to hide fields for when the content is an array
          // comparison is done so that we can hide fields when value is present or not
          // example: ['speedboat', 'alpha'].includes("speedboat") !== true
          evalString = `['${newContent.value[key].join("','")}'].${hideRule.method}("${hideRule.value}") ${hideRule.condition} true`;
        } else {
          evalString = `"${newContent.value[key]}"${hideRule.condition}"${hideRule.value}"`;
        }

        if (eval(evalString)) {
          fieldsToHide = fieldsToHide.concat(hideRule.fields);
        }
      });
    }
    const conditions = store.getters.workspace.settings.conditions ? [...store.getters.workspace.settings.conditions] : [];
    if (fieldConfig && fieldConfig.showWhenConditions && fieldConfig.showWhenConditions.length) {
      const fieldCondition = fieldConfig.showWhenConditions.map(condition => {
        let method = 'some';
        let negation = '';
        if (condition.operator && condition.operator.includes('AND')) {
          method = 'every';
        }

        if (condition.operator && condition.operator.includes('NOT')) {
          method = 'every';
          negation = '!';
        }

        const test = `${negation}condition.conditionValues.${method}(item => conditions.includes(item))`;

        return eval(test);
      });

      if (fieldCondition.every(condition => !condition)) {
        fieldsToHide.push(key);
      }
    }
  });

  // console.log(' DISABLED FIELDS --->>> ', fieldsToHide);
  return fieldsToHide;
});

watch(formValid, val => {
  emit('update:contentValid', val);
});

// watch(props.contentType, val => {
//   panels.value = getPanels(val, props.rawContentTypes);
// });

watch(clonedContent, (newVal, oldVal) => {
  const fieldChanged = Object.keys(difference(newVal, oldVal))[0];
  const field = props.contentType.fields[fieldChanged];

  // ResetWhen feature -> defined on JSON
  if (field && field.resetWhen) {
    const resetRuleFields = Array.isArray(field.resetWhen) ? field.resetWhen : [field.resetWhen];

    resetRuleFields.forEach(r => {
      const evalCondition = (r.condition && r.value) ? `"${newVal[fieldChanged]}" ${r.condition} "${r.value}"` : newVal[fieldChanged];

      if (eval(evalCondition)) {
        Object.entries(r.set).forEach(([key, value]) => {
          Vue.set(newContent.value, key, value);
        });
      }
    });

    // BREAK REACTIVITY !!!!!!
    // newContent.value = { ...newContent.value, ...field.resetWhen.set };
  }
});

onMounted(() => {
  panels.value = getPanels(props.contentType, props.rawContentTypes);
});

</script>

<style lang="scss" scoped>
  /**************************
    Warning Input at Forms
  **************************/
  .alert-border{
      position: relative;
      padding: 0.5rem 1rem 0rem 1rem;
      margin-bottom: 0.5rem;
  }

  .span-position{
      position: absolute;
      right: 0;
      top: 0;
      padding: 5px 10px;
      z-index: 1;
  }

  .warning-border{
       border: 2px solid red;
  }

  .encrypted-border{
    border: 2px solid rgb(146, 137, 6);
  }

  .warning-bg{
    background-color: red;
  }

  .encrypted-bg{
    background-color:  rgb(146, 137, 6);;
  }
</style>
